<template>
  <div class="main-product-swatches main-product__form">
    <div
      v-if="label"
      class="product-form__header"
    >
      <div class="product-form__title">
        {{ label }}:
        <span
          v-if="hoverSwatch || isActive"
          class="product-form__value"
          v-text="hoverSwatch || activeSwatch"
        />
      </div>
      <div
        v-if="isFinalSale && isActive"
        class="product-form__badge badge badge--small"
        v-text="$string('product.extra.final_sale')"
      />
      <div
        v-else-if="isPickaPack && isActive"
        class="product-form__badge badge badge--small"
        v-text="$string(
          'product.extra.pick_a_pack',
          {
            replace: {
              number: pickapack.threshold,
              dis: pickapack.discount
            }
          }
        )"
      />
    </div>

    <div class="form-group">
      <div class="form-group__field">
        <template
          v-for="swatch of visibleSwatches"
          :key="swatch.id"
        >
          <input
            :id="`swatch-${swatch.id}`"
            class="product-form__radio"
            :class="{
              'product-form__radio--active': checkActive(swatch),
              'product-form__radio--disabled': !swatch.available
            }"
            type="radio"
            name="swatch"
            :value="swatch.name"
            @click="swatchUpdated(swatch)"
          >
          <label
            class="
              product-form__label
              product-form__label--swatch
            "
            :for="`swatch-${swatch.id}`"
            :class="{
              'product-form__label--image-loaded': validateImage(swatch.image),
              'product-form__label--swatch-white': swatchIsWhite(swatch)
            }"
            tabindex="0"
            @keyup.enter="swatchUpdated(swatch)"
            @mouseover="setHoverSwatch(swatch.name)"
            @mouseout="setHoverSwatch('')"
          >
            <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
            <img
              class="product-form__swatch-image"
              :src="swatch.image"
              :alt="swatch.name"
              @error="defaultSwatch(swatch)"
              @load="swatchLoaded(swatch)"
            >
          </label>
        </template>

        <a
          v-if="!showAll && swatches.length > swatchLimit"
          class="product-form__view-product"
          :href="product.url"
          :title="$string('product.form.view_product')"
        >
          <icon-plus />
        </a>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product swatches (main-product-swatches)
 * -----------------------------------------------------------------------------
 * Swatch component for use with Main Product.
 * @param {String} label - swatch group label
 * @param {Boolean, Number} limitOverride - Override swatch limit.
 * @param {Object} product - product data
 * @param {Object} swatches - swatch data
 * @param {Object} pickapack - Pick a pack configuration
 * @param {Boolean} showAll - Boolean to dictate whether we show all swatches
 *
 */
import { nextTick } from 'vue'
import { mapState } from 'vuex'
import IconPlus from '~icons/misc/plus.svg'

export default {
  name: 'MainProductSwatches',

  components: {
    IconPlus,
  },

  props: {
    label: {
      type: String,
      default: null,
    },
    limitOverride: {
      type: [Boolean, Number],
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    swatches: {
      type: [Boolean, Object],
      default: false,
    },
    pickapack: {
      type: [Boolean, Object],
      default: false,
    },
    showAll: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'swatch-updated': (payload) => typeof payload === 'object',
    'swatch-loaded': (payload) => typeof payload === 'object',
    'swatches-ready': () => true,
  },

  data() {
    return {
      active: null,
      hoverSwatch: '',
      mobileSwatchLimit: 3,
      desktopSwatchLimit: 5,
      swatchesReady: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute the name of the active color swatch
     * @returns {Boolean, String}
     */
    activeSwatch() {
      if (!this.product) {
        return false
      }

      const activeSwatch = this.product.options.find((option) => {
        return option.name.toLowerCase() === 'color'
      })

      return activeSwatch.values[0]
    },

    /**
     * Compute if product is tagged with finalsale
     * @returns {Boolean}
     */
    isFinalSale() {

      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => {
        const clean = data.toLowerCase()
        return clean === 'finalsale'
      })

      return Boolean(tag)
    },

    /**
     * Compute if product is tagged with pickapack
     * @returns {Boolean}
     */
    isPickaPack() {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => {
        const clean = data.toLowerCase()
        return clean === 'pickapack'
      })

      return Boolean(tag)
    },

    /**
     * Compute if section is active after swatch change.
     * @returns {Boolean}
     */
    isActive() {
      if (!this.active) {
        return false
      }

      return this.product.id === this.active.id
    },

    /**
     * Compute swatch display limit.
     */
    swatchLimit() {
      return this.isMobile ? this.mobileSwatchLimit : this.desktopSwatchLimit
    },

    /**
     * Compute which swatches are visible.
     */
    visibleSwatches() {
      if (this.showAll) {
        return this.swatches
      }

      if (this.limitOverride) {
        return this.swatches.slice(0, this.limitOverride)
      }

      return this.swatches.slice(0, this.swatchLimit)
    },
  },
  watch: {

    /**
     * Watch for first ready swatch and emit state
     */
    swatchesReady() {
      this.$emit('swatches-ready')
    },
  },

  mounted() {
    this.swatchesReady = false
  },

  methods: {

    /**
     * Emit swatch updated event
     * @param {Object} swatch
     */
    swatchUpdated(swatch) {
      this.active = swatch
      this.$emit('swatch-updated', swatch)
    },

    /**
     * Emit swatch image loaded event
     * @param {Object} swatch
     */
    swatchLoaded(swatch) {
      this.$emit('swatch-loaded', swatch)
    },

    /**
     * Validate image url
     * @param {String} url
     * @param {Return} boolean
     */
    validateImage(url) {
      const img = new Image()

      nextTick().then(() => {
        img.src = url
      })

      if (img.complete) {
        this.swatchesReady = true
        return true
      }

      img.onload = () => {
        this.swatchesReady = true
        return true
      }

      return false
    },

    /**
     * Check if swatch is active on load
     * @param {Object}
     * @return {Object}
     */
    checkActive(swatch) {
      const active = swatch.id === this.product.id

      if (active) {
        this.active = swatch
      }

      return active
    },

    /**
     * Set hover swatch name to passed value
     * @param {String} swatchName
     */
    setHoverSwatch(swatchName) {
      this.hoverSwatch = swatchName
    },

    /**
     * Determine if swatch is white.
     */
    swatchIsWhite(swatch) {
      if (!swatch.name) {
        return false
      }

      return swatch.name.toLowerCase().includes('white')
    },

    defaultSwatch(swatch) {
      swatch.image = '//dereklam.com/cdn/shop/files/color-grey_32x10.png'
    },
  },
}
</script>
